import { FC, useEffect, useRef, useState, SyntheticEvent } from 'react';
import {
    Tooltip as BaseTooltip,
    TooltipProps as BaseTooltipProps,
} from '@servicetitan/design-system';
import { observer } from 'mobx-react';
import { useDependencies } from '@servicetitan/react-ioc';
import { tokens } from '@servicetitan/tokens/core';

import { TooltipsStore } from '../stores/tooltips.store';

interface TooltipProps extends BaseTooltipProps {
    contentId?: string;
}

// TODO: clean the component after the implementation https://github.com/servicetitan/anvil/issues/1768
export const Tooltip: FC<TooltipProps> = observer(({ contentId, hoverOnly, open, ...props }) => {
    const text = useDependencies(TooltipsStore)[0].tooltips[contentId as string];

    const trackShow = () => {
        if ((window as any).pendo && text) {
            (window as any).pendo.track('TooltipIsShown', { tooltipId: contentId });
        }
    };

    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const showTimer = useRef<any>(null);
    const hideTimer = useRef<any>(null);

    /* Delay prevents unintentional hover triggering */
    const delay: number = parseInt(tokens.durationSlow, 10); // '300ms' -> 300

    useEffect(() => {
        return () => {
            clearTimeout(hideTimer.current);
            clearTimeout(showTimer.current);
        };
    }, []);

    const showTooltip = (e: SyntheticEvent) => {
        if (e.type === 'mouseenter') {
            showTimer.current = setTimeout(() => {
                setHovered(true);
                trackShow();
            }, delay);
            clearTimeout(hideTimer.current);
            return;
        }
        if (e.type === 'focus' && !hoverOnly) {
            showTimer.current = setTimeout(() => {
                setFocused(true);
                trackShow();
            }, delay);
            clearTimeout(hideTimer.current);
        }
    };

    const hideTooltip = (e: SyntheticEvent) => {
        if (e.type === 'mouseleave') {
            hideTimer.current = setTimeout(() => setHovered(false), delay);
            clearTimeout(showTimer.current);
            return;
        }
        if (e.type === 'blur' && !hoverOnly) {
            hideTimer.current = setTimeout(() => setFocused(false), delay);
            clearTimeout(showTimer.current);
        }
    };

    const tooltipVisible = Boolean((text || props.text) && (hovered || focused || open));

    return (
        <BaseTooltip
            text={text || props.text}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
            open={tooltipVisible}
            {...props}
        />
    );
});
